import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { format } from 'date-fns';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function Suscripciones() {
  const { usuario } = useAuth();
  const [subs, setSubs] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [fincas, setFincas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState('todas');
  const [busqueda, setBusqueda] = useState('');

  const [modalCrear, setModalCrear] = useState(false);
  const [formCrear, setFormCrear] = useState({
    finca_id: '', plan_id: 'basico', ciclo: 'mensual', notas: '',
  });
  const [guardando, setGuardando] = useState(false);

  const [modalEditar, setModalEditar] = useState(null);

  if (usuario && usuario.rol !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [subsRes, planesRes, fincasRes] = await Promise.all([
        api.get('/suscripciones'),
        api.get('/planes'),
        api.get('/fincas'),
      ]);
      setSubs(subsRes.data || []);
      setPlanes(planesRes.data || []);
      setFincas(fincasRes.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  // KPIs
  const kpis = useMemo(() => {
    const hoy = new Date();
    let activas = 0, porVencer = 0, vencidas = 0, enGracia = 0;
    for (const s of subs) {
      const venc = new Date(s.fecha_vencimiento);
      const ms = venc - hoy;
      const diasVencida = Math.floor((hoy - venc) / 86400000);
      if (s.estado === 'cancelada') continue;
      if (ms > 7 * 86400000) activas++;
      else if (ms > 0) porVencer++;
      else if (diasVencida <= 14) enGracia++;
      else vencidas++;
    }
    return { total: subs.length, activas, porVencer, vencidas, enGracia };
  }, [subs]);

  const canceladasCount = useMemo(
    () => subs.filter((s) => s.estado === 'cancelada').length,
    [subs]
  );

  const subsFiltradas = useMemo(() => {
    let lista = [...subs];
    const hoy = new Date();
    if (filtro === 'activas') {
      lista = lista.filter((s) => s.estado !== 'cancelada' && new Date(s.fecha_vencimiento) - hoy > 7 * 86400000);
    } else if (filtro === 'por_vencer') {
      lista = lista.filter((s) => {
        const ms = new Date(s.fecha_vencimiento) - hoy;
        return s.estado !== 'cancelada' && ms > 0 && ms <= 7 * 86400000;
      });
    } else if (filtro === 'en_gracia') {
      lista = lista.filter((s) => {
        const ms = new Date(s.fecha_vencimiento) - hoy;
        return s.estado !== 'cancelada' && ms <= 0 && ms > -14 * 86400000;
      });
    } else if (filtro === 'vencidas') {
      lista = lista.filter((s) => {
        const ms = new Date(s.fecha_vencimiento) - hoy;
        return s.estado !== 'cancelada' && ms <= -14 * 86400000;
      });
    } else if (filtro === 'canceladas') {
      lista = lista.filter((s) => s.estado === 'cancelada');
    }
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      lista = lista.filter((s) =>
        s.finca_nombre?.toLowerCase().includes(q) ||
        s.plan_nombre?.toLowerCase().includes(q)
      );
    }
    return lista;
  }, [subs, filtro, busqueda]);

  const badgeEstado = (s) => {
    if (s.estado === 'cancelada') {
      return <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-100 text-gray-600">CANCELADA</span>;
    }
    const hoy = new Date();
    const venc = new Date(s.fecha_vencimiento);
    const ms = venc - hoy;
    if (ms > 7 * 86400000) return <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-green-100 text-green-800">ACTIVA</span>;
    if (ms > 0) return <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800">POR VENCER</span>;
    if (ms > -14 * 86400000) return <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-orange-100 text-orange-800">EN GRACIA</span>;
    return <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-red-100 text-red-800">VENCIDA</span>;
  };

  const crear = async () => {
    setGuardando(true);
    try {
      await api.post('/suscripciones', formCrear);
      setModalCrear(false);
      setFormCrear({ finca_id: '', plan_id: 'basico', ciclo: 'mensual', notas: '' });
      await cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al asignar');
    } finally {
      setGuardando(false);
    }
  };

  const renovar = async (s) => {
    if (!confirm(`¿Renovar la suscripción de "${s.finca_nombre}" un ciclo más?`)) return;
    try {
      await api.post(`/suscripciones/${s.id}/renovar`);
      await cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al renovar');
    }
  };

  const cancelar = async (s) => {
    if (!confirm(`¿Cancelar la suscripción de "${s.finca_nombre}"?\n\nLa finca quedará sin plan.`)) return;
    try {
      await api.put(`/suscripciones/${s.id}/cancelar`);
      await cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al cancelar');
    }
  };

  // ✅ Eliminar UNA fila cancelada del historial
  const eliminarFila = async (s) => {
    const ok = confirm(
      `¿Eliminar del historial la suscripción cancelada de "${s.finca_nombre}"?\n\n` +
      `Esta acción no se puede deshacer.`
    );
    if (!ok) return;
    try {
      await api.delete(`/suscripciones/${s.id}`);
      await cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar');
    }
  };

  // ✅ Eliminar TODAS las canceladas
  const limpiarCanceladas = async () => {
    if (canceladasCount === 0) return;
    const ok = confirm(
      `⚠️ ELIMINAR ${canceladasCount} SUSCRIPCIÓN(ES) CANCELADA(S)\n\n` +
      `Se borrarán permanentemente del historial.\n` +
      `Esta acción no se puede deshacer.\n\n` +
      `¿Confirmas?`
    );
    if (!ok) return;
    try {
      const { data } = await api.delete('/suscripciones/canceladas');
      alert(`✅ ${data.mensaje}`);
      await cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar canceladas');
    }
  };

  const guardarEdicion = async () => {
    setGuardando(true);
    try {
      await api.put(`/suscripciones/${modalEditar.id}`, {
        plan_id: modalEditar.plan_id,
        ciclo: modalEditar.ciclo,
        estado: modalEditar.estado,
        auto_renovar: modalEditar.auto_renovar,
        notas: modalEditar.notas,
      });
      setModalEditar(null);
      await cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-700">Suscripciones</h1>
          <p className="text-sm text-gray-500 mt-1">
            Asigna y gestiona el plan comercial de cada finca.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={cargar}
            className="px-4 py-2 border border-slate-400 text-slate-700 rounded-lg hover:bg-slate-100 text-sm"
          >
            🔄 Actualizar
          </button>
          {/* ✅ Botón limpiar canceladas (solo aparece si hay) */}
          {canceladasCount > 0 && (
            <button
              onClick={limpiarCanceladas}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-medium"
              title="Elimina permanentemente todas las suscripciones canceladas"
            >
              🗑 Limpiar canceladas ({canceladasCount})
            </button>
          )}
          <button
            onClick={() => setModalCrear(true)}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 text-sm font-medium"
          >
            + Asignar plan
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <button onClick={() => setFiltro('activas')} className={`text-left bg-white p-4 rounded-xl shadow-sm border-2 transition hover:shadow-md ${filtro === 'activas' ? 'border-green-500' : 'border-transparent'}`}>
          <div className="text-xs text-gray-500">Activas</div>
          <div className="text-3xl font-bold text-green-600">{kpis.activas}</div>
        </button>
        <button onClick={() => setFiltro('por_vencer')} className={`text-left bg-white p-4 rounded-xl shadow-sm border-2 transition hover:shadow-md ${filtro === 'por_vencer' ? 'border-amber-500' : 'border-transparent'}`}>
          <div className="text-xs text-gray-500">Por vencer (≤7d)</div>
          <div className="text-3xl font-bold text-amber-600">{kpis.porVencer}</div>
        </button>
        <button onClick={() => setFiltro('en_gracia')} className={`text-left bg-white p-4 rounded-xl shadow-sm border-2 transition hover:shadow-md ${filtro === 'en_gracia' ? 'border-orange-500' : 'border-transparent'}`}>
          <div className="text-xs text-gray-500">En gracia</div>
          <div className="text-3xl font-bold text-orange-600">{kpis.enGracia}</div>
        </button>
        <button onClick={() => setFiltro('vencidas')} className={`text-left bg-white p-4 rounded-xl shadow-sm border-2 transition hover:shadow-md ${filtro === 'vencidas' ? 'border-red-500' : 'border-transparent'}`}>
          <div className="text-xs text-gray-500">Vencidas</div>
          <div className="text-3xl font-bold text-red-600">{kpis.vencidas}</div>
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="🔍 Buscar finca o plan..."
          className="flex-1 min-w-[240px] px-3 py-2 border rounded-lg text-sm"
        />
        <select
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm"
        >
          <option value="todas">Todas</option>
          <option value="activas">Activas</option>
          <option value="por_vencer">Por vencer</option>
          <option value="en_gracia">En gracia</option>
          <option value="vencidas">Vencidas</option>
          <option value="canceladas">Canceladas</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4">{error}</div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {cargando ? (
          <div className="p-12 text-center text-gray-500">Cargando...</div>
        ) : subsFiltradas.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            {subs.length === 0
              ? 'Aún no hay suscripciones. Toca "+ Asignar plan".'
              : 'No hay suscripciones con ese filtro.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Finca</th>
                  <th className="text-left px-4 py-3 font-semibold">Plan</th>
                  <th className="text-center px-4 py-3 font-semibold">Ciclo</th>
                  <th className="text-center px-4 py-3 font-semibold">Estado</th>
                  <th className="text-center px-4 py-3 font-semibold">Vence</th>
                  <th className="text-center px-4 py-3 font-semibold">Uso</th>
                  <th className="text-right px-4 py-3 font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {subsFiltradas.map((s) => {
                  const usoPisc = `${s.piscinas_actuales ?? 0}/${s.limite_piscinas ?? '∞'}`;
                  const usoUser = `${s.usuarios_actuales ?? 0}/${s.limite_usuarios ?? '∞'}`;
                  const esCancelada = s.estado === 'cancelada';
                  return (
                    <tr key={s.id} className={`border-t hover:bg-slate-50 ${esCancelada ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3 font-semibold text-slate-800">
                        <div className="flex items-center gap-2">
                          <span>{s.finca_nombre}</span>
                          {s.es_demo && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                              TRIAL
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{s.plan_nombre}</div>
                        <div className="text-xs text-gray-500">
                          ${Number(s.precio_mensual).toFixed(0)}/mes
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-xs uppercase text-gray-600">
                        {s.ciclo}
                      </td>
                      <td className="px-4 py-3 text-center">{badgeEstado(s)}</td>
                      <td className="px-4 py-3 text-center text-xs">
                        {format(new Date(s.fecha_vencimiento), 'dd/MM/yyyy')}
                        <div className={`text-[10px] ${s.dias_restantes < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                          {s.dias_restantes != null ? `${s.dias_restantes} d` : '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-xs">
                        <div>🏞️ {usoPisc}</div>
                        <div>👥 {usoUser}</div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {esCancelada ? (
                          // ✅ Fila cancelada: solo botón Eliminar
                          <button
                            onClick={() => eliminarFila(s)}
                            className="text-xs px-2 py-1 text-red-600 border border-red-300 rounded hover:bg-red-50"
                            title="Eliminar del historial"
                          >
                            🗑 Eliminar
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => renovar(s)}
                              className="text-xs px-2 py-1 text-green-700 border border-green-300 rounded hover:bg-green-50 mr-1"
                              title="Renovar un ciclo más"
                            >
                              🔄 Renovar
                            </button>
                            <button
                              onClick={() => setModalEditar({ ...s })}
                              className="text-xs px-2 py-1 text-slate-700 border border-slate-300 rounded hover:bg-slate-100 mr-1"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => cancelar(s)}
                              className="text-xs px-2 py-1 text-red-600 border border-red-300 rounded hover:bg-red-50"
                              title="Cancelar"
                            >
                              ✕
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal crear */}
      {modalCrear && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={() => !guardando && setModalCrear(false)}
        >
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 bg-slate-700 text-white flex justify-between items-center rounded-t-2xl">
              <h2 className="text-base font-bold">Asignar plan a finca</h2>
              <button onClick={() => !guardando && setModalCrear(false)} className="w-8 h-8 rounded-full hover:bg-white/10">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-slate-700">Finca *</span>
                <select
                  value={formCrear.finca_id}
                  onChange={(e) => setFormCrear((f) => ({ ...f, finca_id: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">— Seleccionar —</option>
                  {fincas.map((fi) => (
                    <option key={fi.id} value={fi.id}>{fi.nombre}</option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-700">Plan *</span>
                <select
                  value={formCrear.plan_id}
                  onChange={(e) => setFormCrear((f) => ({ ...f, plan_id: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                >
                  {planes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre} · ${Number(p.precio_mensual).toFixed(0)}/mes
                    </option>
                  ))}
                </select>
                <span className="text-[11px] text-gray-500">
                  "Gratis" solo se puede asignar a fincas trial (no a permanentes).
                </span>
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-700">Ciclo *</span>
                <select
                  value={formCrear.ciclo}
                  onChange={(e) => setFormCrear((f) => ({ ...f, ciclo: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="mensual">Mensual</option>
                  <option value="anual">Anual (2 meses gratis)</option>
                </select>
              </label>

              <label className="block">
                <span className="text-xs font-medium text-slate-700">Notas</span>
                <textarea
                  value={formCrear.notas}
                  onChange={(e) => setFormCrear((f) => ({ ...f, notas: e.target.value }))}
                  rows={2}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="Ej: pago por transferencia Banco Pichincha"
                />
              </label>

              <div className="flex justify-end gap-2 pt-3 border-t">
                <button
                  onClick={() => !guardando && setModalCrear(false)}
                  disabled={guardando}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100"
                >
                  Cancelar
                </button>
                <button
                  onClick={crear}
                  disabled={guardando || !formCrear.finca_id}
                  className="px-6 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
                >
                  {guardando ? 'Asignando...' : 'Asignar plan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal editar */}
      {modalEditar && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={() => !guardando && setModalEditar(null)}
        >
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 bg-slate-700 text-white flex justify-between items-center rounded-t-2xl">
              <div>
                <h2 className="text-base font-bold">Editar suscripción</h2>
                <p className="text-xs text-slate-300">{modalEditar.finca_nombre}</p>
              </div>
              <button onClick={() => !guardando && setModalEditar(null)} className="w-8 h-8 rounded-full hover:bg-white/10">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-slate-700">Plan</span>
                <select
                  value={modalEditar.plan_id}
                  onChange={(e) => setModalEditar((s) => ({ ...s, plan_id: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                >
                  {planes.map((p) => (
                    <option key={p.id} value={p.id}>{p.nombre}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-700">Ciclo</span>
                <select
                  value={modalEditar.ciclo}
                  onChange={(e) => setModalEditar((s) => ({ ...s, ciclo: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="mensual">Mensual</option>
                  <option value="anual">Anual</option>
                </select>
                <span className="text-[11px] text-gray-500">Al cambiar plan o ciclo se recalcula el vencimiento.</span>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-700">Estado</span>
                <select
                  value={modalEditar.estado}
                  onChange={(e) => setModalEditar((s) => ({ ...s, estado: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="activa">Activa</option>
                  <option value="gracia">En gracia</option>
                  <option value="vencida">Vencida</option>
                  <option value="cancelada">Cancelada</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={modalEditar.auto_renovar}
                  onChange={(e) => setModalEditar((s) => ({ ...s, auto_renovar: e.target.checked }))}
                  className="w-4 h-4"
                />
                <span>Auto-renovar</span>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-700">Notas</span>
                <textarea
                  value={modalEditar.notas || ''}
                  onChange={(e) => setModalEditar((s) => ({ ...s, notas: e.target.value }))}
                  rows={2}
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                />
              </label>
              <div className="flex justify-end gap-2 pt-3 border-t">
                <button
                  onClick={() => !guardando && setModalEditar(null)}
                  disabled={guardando}
                  className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-100"
                >
                  Cancelar
                </button>
                <button
                  onClick={guardarEdicion}
                  disabled={guardando}
                  className="px-6 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
                >
                  {guardando ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}